import {
  CHAT_LIST_SEARCH_QUERY_ACTION_APPLY_ACTIVE,
  CHAT_LIST_SEARCH_QUERY_ACTION_CLEAR,
  CHAT_LIST_SEARCH_QUERY_ACTION_NOOP,
} from './chatListFetchState';

export const runChatListRouteChange = ({
  activeSearchQuery = '',
  clearSearchResults = null,
  isLoadingLabel = false,
  isSearchModeActive = false,
  loadSearchResults = null,
  newRoute = null,
  oldRoute = null,
  resetAndFetchData = null,
  resetReason = '',
  resolveRouteChange = null,
  setActiveStatus = null,
} = {}) => {
  if (typeof resolveRouteChange !== 'function') {
    return { routeChange: null, status: 'invalid' };
  }

  const routeChange = resolveRouteChange({
    activeSearchQuery,
    isLoadingLabel,
    isSearchModeActive,
    newRoute,
    oldRoute,
  });

  if (
    routeChange.shouldClearSearch &&
    typeof clearSearchResults === 'function'
  ) {
    clearSearchResults();
  }

  if (routeChange.action === 'load-search-results') {
    if (typeof loadSearchResults === 'function') {
      loadSearchResults(routeChange.searchQuery, 1);
      return { routeChange, status: 'search-loaded' };
    }

    return { routeChange, status: 'invalid' };
  }

  if (routeChange.hasSearchQuery) {
    return { routeChange, status: 'search-query-active' };
  }

  if (routeChange.action === 'status-change') {
    if (typeof setActiveStatus === 'function') {
      setActiveStatus(routeChange.status);
    }
    if (
      routeChange.shouldResetFetch &&
      typeof resetAndFetchData === 'function'
    ) {
      resetAndFetchData(resetReason);
    }
    return { routeChange, status: 'status-changed' };
  }

  if (routeChange.shouldResetFetch) {
    if (typeof resetAndFetchData === 'function') {
      resetAndFetchData(resetReason);
      return { routeChange, status: 'reset' };
    }

    return { routeChange, status: 'invalid' };
  }

  return { routeChange, status: 'skipped' };
};

export const resolveChatListFiltersFromUISettings = ({
  defaultSortBy = '',
  defaultStatus = '',
  routeStatus = null,
  sortByKeys = [],
  uiSettings = {},
} = {}) => {
  const filterBy = uiSettings?.conversations_filter_by || {};
  const status = routeStatus || filterBy.status || defaultStatus;
  const orderBy = filterBy.order_by;
  const safeKeys = Array.isArray(sortByKeys) ? sortByKeys : [];
  const sortBy = safeKeys.includes(orderBy) ? orderBy : defaultSortBy;
  return {
    activeStatus: status,
    activeSortBy: sortBy,
    shouldEnableUnreadFirst: Boolean(uiSettings?.unread_first_enabled),
    shouldEnablePriorityFilter: Boolean(uiSettings?.priority_filter_enabled),
  };
};

export const runChatListFiltersFromUISettingsApply = ({
  defaultSortBy = '',
  defaultStatus = '',
  routeStatus = null,
  resolveFilters = resolveChatListFiltersFromUISettings,
  setActiveSortBy = null,
  setActiveStatus = null,
  setChatListPriorityFilter = null,
  setChatListUnreadFirst = null,
  setPriorityFilterEnabled = null,
  setUnreadFirstEnabled = null,
  sortByKeys = [],
  uiSettings = {},
} = {}) => {
  if (typeof resolveFilters !== 'function') {
    return { filtersUpdate: null, status: 'invalid' };
  }

  const filtersUpdate = resolveFilters({
    defaultSortBy,
    defaultStatus,
    ...(routeStatus ? { routeStatus } : {}),
    sortByKeys,
    uiSettings,
  });

  if (typeof setActiveStatus === 'function') {
    setActiveStatus(filtersUpdate.activeStatus);
  }
  if (typeof setActiveSortBy === 'function') {
    setActiveSortBy(filtersUpdate.activeSortBy);
  }

  if (typeof setUnreadFirstEnabled === 'function') {
    setUnreadFirstEnabled(filtersUpdate.shouldEnableUnreadFirst);
  }
  if (typeof setChatListUnreadFirst === 'function') {
    setChatListUnreadFirst(filtersUpdate.shouldEnableUnreadFirst);
  }
  if (typeof setPriorityFilterEnabled === 'function') {
    setPriorityFilterEnabled(filtersUpdate.shouldEnablePriorityFilter);
  }
  if (typeof setChatListPriorityFilter === 'function') {
    setChatListPriorityFilter(filtersUpdate.shouldEnablePriorityFilter);
  }

  return { filtersUpdate, status: 'applied' };
};

export const resolveChatListContactFilterQuery = ({
  contactId = null,
  contactName = '',
} = {}) => {
  const trimmedName = typeof contactName === 'string' ? contactName.trim() : '';
  if (trimmedName) {
    return trimmedName;
  }
  return contactId === null || contactId === undefined ? '' : String(contactId);
};

export const resolveChatListSearchQueryChangeAction = ({
  isSearchModeActive = false,
  query = '',
} = {}) => {
  if (!query) {
    return CHAT_LIST_SEARCH_QUERY_ACTION_CLEAR;
  }
  if (isSearchModeActive) {
    return CHAT_LIST_SEARCH_QUERY_ACTION_APPLY_ACTIVE;
  }
  return CHAT_LIST_SEARCH_QUERY_ACTION_NOOP;
};

export const runChatListSearchQueryChange = ({
  clearSearchResults = null,
  isSearchModeActive = false,
  query = '',
  setActiveSearchQuery = null,
  setSearchQuery = null,
} = {}) => {
  if (typeof setSearchQuery === 'function') {
    setSearchQuery(query);
  }

  const action = resolveChatListSearchQueryChangeAction({
    isSearchModeActive,
    query,
  });

  if (action === CHAT_LIST_SEARCH_QUERY_ACTION_CLEAR) {
    if (typeof clearSearchResults === 'function') {
      clearSearchResults();
    }
    return { action };
  }

  if (
    action === CHAT_LIST_SEARCH_QUERY_ACTION_APPLY_ACTIVE &&
    typeof setActiveSearchQuery === 'function'
  ) {
    setActiveSearchQuery(query);
  }

  return { action };
};

export const buildChatListSearchQueryRouteUpdate = ({
  currentQuery = null,
  query = '',
} = {}) => {
  const safeQuery =
    currentQuery && typeof currentQuery === 'object' ? currentQuery : {};
  if (safeQuery.q === query) {
    return null;
  }
  return { ...safeQuery, q: query };
};

export const runChatListSearchRouteQuerySync = async ({
  currentQuery = null,
  query = '',
  replaceRouteQuery = null,
  skipUnchanged = false,
} = {}) => {
  try {
    const nextQuery = skipUnchanged
      ? buildChatListSearchQueryRouteUpdate({ currentQuery, query })
      : { ...(currentQuery || {}), q: query };

    if (!nextQuery) {
      return { nextQuery: null, replaced: false, status: 'skipped' };
    }

    if (typeof replaceRouteQuery !== 'function') {
      return { nextQuery, replaced: false, status: 'invalid' };
    }

    const replaced = Boolean(await replaceRouteQuery(nextQuery));
    return {
      nextQuery,
      replaced,
      status: replaced ? 'synced' : 'failed',
    };
  } catch {
    return { nextQuery: null, replaced: false, status: 'failed' };
  }
};
